package com.daytimegaming.ourdailyfinances.presentation.home

import androidx.compose.foundation.background
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.Sort
import androidx.compose.material.icons.filled.ArrowDownward
import androidx.compose.material.icons.filled.ArrowUpward
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.tooling.preview.PreviewLightDark
import androidx.compose.ui.tooling.preview.PreviewParameter
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.daytimegaming.ourdailyfinances.presentation.preview.RecurringTransactionsPreviewParameterProvider
import com.daytimegaming.ourdailyfinances.presentation.theme.AppTheme
import com.daytimegaming.ourdailyfinances.presentation.theme.GlassCard
import com.daytimegaming.ourdailyfinances.presentation.util.formatAmount
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun RecurringTransactionsScreen(
    onNavigateBack: () -> Unit,
    onStreamClick: (String) -> Unit,
    viewModel: RecurringTransactionsViewModel = koinViewModel()
) {
    val state by viewModel.state.collectAsStateWithLifecycle()

    LaunchedEffect(Unit) {
        viewModel.load()
    }

    RecurringTransactionsScreenContent(
        state = state,
        onNavigateBack = onNavigateBack,
        onStreamClick = onStreamClick,
        onCycleSort = { viewModel.cycleSortType() }
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun RecurringTransactionsScreenContent(
    state: RecurringTransactionsScreenState,
    onNavigateBack: () -> Unit,
    onStreamClick: (String) -> Unit,
    onCycleSort: () -> Unit = {}
) {
    Scaffold(
        modifier = Modifier.fillMaxSize(),
        topBar = {
            TopAppBar(
                title = {
                    Text(
                        text = "Recurring Subscriptions",
                        style = MaterialTheme.typography.headlineLarge,
                        color = MaterialTheme.colorScheme.onSurface
                    )
                },
                navigationIcon = {
                    IconButton(onClick = onNavigateBack) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = "Back",
                            tint = MaterialTheme.colorScheme.onSurface
                        )
                    }
                },
                actions = {
                    if (state is RecurringTransactionsScreenState.Loaded) {
                        val icon = when (state.sortType) {
                            RecurringSortType.RECENT -> Icons.AutoMirrored.Filled.Sort
                            RecurringSortType.HIGHEST -> Icons.Default.ArrowDownward
                            RecurringSortType.LOWEST -> Icons.Default.ArrowUpward
                        }
                        IconButton(onClick = onCycleSort) {
                            Icon(
                                imageVector = icon,
                                contentDescription = "Sort",
                                tint = MaterialTheme.colorScheme.onSurface
                            )
                        }
                    }
                }
            )
        }
    ) { contentPadding ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(contentPadding)
        ) {
            when (state) {
                is RecurringTransactionsScreenState.Loading -> {
                    Box(
                        modifier = Modifier.fillMaxSize(),
                        contentAlignment = Alignment.Center
                    ) {
                        CircularProgressIndicator(color = MaterialTheme.colorScheme.primary)
                    }
                }
                is RecurringTransactionsScreenState.Error -> {
                    Box(
                        modifier = Modifier.fillMaxSize(),
                        contentAlignment = Alignment.Center
                    ) {
                        Text(
                            text = state.message,
                            color = MaterialTheme.colorScheme.error,
                            style = MaterialTheme.typography.bodyMedium
                        )
                    }
                }
                is RecurringTransactionsScreenState.Loaded -> {
                    val activeStreams = remember(state.streams, state.sortType) {
                        state.streams
                            .filter { it.streamType == "outflow" && it.isActive }
                            .sortedBySortType(state.sortType)
                    }

                    val inactiveStreams = remember(state.streams, state.sortType) {
                        state.streams
                            .filter { it.streamType == "outflow" && !it.isActive }
                            .sortedBySortType(state.sortType)
                    }

                    val monthlyOutflow = remember(activeStreams) {
                        var outflow = 0.0
                        activeStreams.forEach { stream ->
                            val multiplier = when (stream.frequency) {
                                "WEEKLY" -> 52.0 / 12.0
                                "BIWEEKLY" -> 26.0 / 12.0
                                "SEMI_MONTHLY" -> 2.0
                                "MONTHLY" -> 1.0
                                else -> 1.0
                            }
                            val monthlyEquivalent = stream.averageAmount.amount * multiplier
                            outflow += monthlyEquivalent
                        }
                        outflow
                    }

                    LazyColumn(
                        modifier = Modifier.fillMaxSize(),
                        contentPadding = PaddingValues(16.dp),
                        verticalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        // Summary Card
                        item(key = "summary") {
                            GlassCard(modifier = Modifier.fillMaxWidth()) {
                                Column {
                                    Text(
                                        text = "Monthly Subscription Summary",
                                        style = MaterialTheme.typography.titleMedium,
                                        fontWeight = FontWeight.Bold,
                                        color = MaterialTheme.colorScheme.onSurface
                                    )
                                    Spacer(modifier = Modifier.height(16.dp))

                                    Column {
                                        Text(
                                            text = "Total Monthly Bills",
                                            style = MaterialTheme.typography.labelSmall,
                                            color = MaterialTheme.colorScheme.onSurfaceVariant
                                        )
                                        Spacer(modifier = Modifier.height(4.dp))
                                        Text(
                                            text = "$${monthlyOutflow.formatAmount()}",
                                            style = MaterialTheme.typography.headlineLarge,
                                            fontWeight = FontWeight.Bold,
                                            color = MaterialTheme.colorScheme.error
                                        )
                                    }
                                }
                            }
                        }

                        // Active Subscriptions Header
                        item(key = "active_header") {
                            Text(
                                text = "Active Subscriptions (${activeStreams.size})",
                                style = MaterialTheme.typography.titleMedium,
                                fontWeight = FontWeight.Bold,
                                color = MaterialTheme.colorScheme.onSurface,
                                modifier = Modifier.padding(horizontal = 4.dp)
                            )
                        }

                        // Active Stream Cards
                        if (activeStreams.isEmpty()) {
                            item(key = "no_active") {
                                Box(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .padding(vertical = 16.dp),
                                    contentAlignment = Alignment.Center
                                ) {
                                    Text(
                                        text = "No active recurring subscriptions found.",
                                        style = MaterialTheme.typography.bodyMedium,
                                        color = MaterialTheme.colorScheme.onSurfaceVariant
                                    )
                                }
                            }
                        } else {
                            items(activeStreams, key = { "active_${it.streamId}" }) { stream ->
                                val accountName = state.accountNames[stream.accountId] ?: "Unknown Account"
                                RecurringStreamItemCard(
                                    stream = stream,
                                    accountName = accountName,
                                    onClick = { onStreamClick(stream.streamId) }
                                )
                            }
                        }

                        // Inactive Subscriptions Header & Cards
                        if (inactiveStreams.isNotEmpty()) {
                            item(key = "inactive_header") {
                                Spacer(modifier = Modifier.height(8.dp))
                                Text(
                                    text = "Inactive Subscriptions (${inactiveStreams.size})",
                                    style = MaterialTheme.typography.titleMedium,
                                    fontWeight = FontWeight.Bold,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                                    modifier = Modifier.padding(horizontal = 4.dp)
                                )
                            }

                            items(inactiveStreams, key = { "inactive_${it.streamId}" }) { stream ->
                                val accountName = state.accountNames[stream.accountId] ?: "Unknown Account"
                                RecurringStreamItemCard(
                                    stream = stream,
                                    accountName = accountName,
                                    onClick = { onStreamClick(stream.streamId) }
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun RecurringStreamItemCard(
    stream: com.daytimegaming.ourdailyfinances.domain.model.RecurringTransactionStream,
    accountName: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    GlassCard(
        modifier = modifier.fillMaxWidth(),
        onClick = onClick
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = if (stream.merchantName.isNullOrBlank()) stream.description else stream.merchantName,
                    style = MaterialTheme.typography.bodyLarge,
                    fontWeight = FontWeight.Bold,
                    color = if (stream.isActive) MaterialTheme.colorScheme.onSurface else MaterialTheme.colorScheme.onSurfaceVariant
                )
                Spacer(modifier = Modifier.height(2.dp))
                Text(
                    text = accountName,
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
                Spacer(modifier = Modifier.height(4.dp))
                Row(
                    horizontalArrangement = Arrangement.spacedBy(6.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Box(
                        modifier = Modifier
                            .background(
                                color = Color(0x1FBEC6E0),
                                shape = RoundedCornerShape(999.dp)
                            )
                            .padding(horizontal = 8.dp, vertical = 2.dp)
                    ) {
                        Text(
                            text = stream.frequency.lowercase().replaceFirstChar { if (it.isLowerCase()) it.titlecase() else it.toString() },
                            style = MaterialTheme.typography.labelSmall,
                            color = MaterialTheme.colorScheme.secondary
                        )
                    }

                    if (!stream.isActive) {
                        Box(
                            modifier = Modifier
                                .background(
                                    color = MaterialTheme.colorScheme.errorContainer,
                                    shape = RoundedCornerShape(999.dp)
                                )
                                .padding(horizontal = 8.dp, vertical = 2.dp)
                        ) {
                            Text(
                                text = "Inactive",
                                style = MaterialTheme.typography.labelSmall,
                                color = MaterialTheme.colorScheme.onErrorContainer
                            )
                        }
                    }
                }
            }

            Spacer(modifier = Modifier.width(12.dp))

            Column(horizontalAlignment = Alignment.End) {
                Text(
                    text = "-$${stream.averageAmount.amount.formatAmount()}",
                    style = MaterialTheme.typography.bodyLarge,
                    fontWeight = FontWeight.Bold,
                    color = if (stream.isActive) MaterialTheme.colorScheme.onSurface else MaterialTheme.colorScheme.onSurfaceVariant
                )
                stream.predictedNextDate?.let { nextDate ->
                    Spacer(modifier = Modifier.height(4.dp))
                    Text(
                        text = "Next: $nextDate",
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
            }
        }
    }
}

private fun List<com.daytimegaming.ourdailyfinances.domain.model.RecurringTransactionStream>.sortedBySortType(
    sortType: RecurringSortType
): List<com.daytimegaming.ourdailyfinances.domain.model.RecurringTransactionStream> {
    return when (sortType) {
        RecurringSortType.RECENT -> sortedByDescending { it.predictedNextDate ?: it.lastDate }
        RecurringSortType.HIGHEST -> sortedByDescending { it.averageAmount.amount }
        RecurringSortType.LOWEST -> sortedBy { it.averageAmount.amount }
    }
}

@PreviewLightDark
@Composable
fun RecurringTransactionsScreenPreview(
    @PreviewParameter(RecurringTransactionsPreviewParameterProvider::class) state: RecurringTransactionsScreenState
) {
    AppTheme(
        darkTheme = isSystemInDarkTheme(),
        dynamicColor = false,
    ) {
        Surface {
            RecurringTransactionsScreenContent(
                state = state,
                onNavigateBack = {},
                onStreamClick = {}
            )
        }
    }
}
